import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingDown, Wallet, Receipt, CalendarClock } from "lucide-react";

const LOSS_RATE = 0.12;

const brl = (v) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

export const RoiCalculator = ({ lostCarts, onLostCartsChange, price, onPriceChange }) => {
  const [fleet, setFleet] = useState(20);
  const [ticketUnits, setTicketUnits] = useState(20);

  const syncLost = (f) => onLostCartsChange(Math.max(1, Math.round((f * LOSS_RATE) / 12)));

  const handleFleet = (v) => {
    setFleet(v);
    setTicketUnits(Math.min(100, Math.max(1, v)));
    syncLost(v);
  };

  const handleTicketUnits = (u) => {
    setTicketUnits(u);
    setFleet(u);
    syncLost(u);
  };

  const ticket = ticketUnits * 2300;

  const monthlyLoss = fleet * price;

  return (
    <section id="economia" className="relative py-24 sm:py-32" data-testid="roi-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[28rem] w-[40rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.1)_0%,rgba(30,58,138,0)_70%)]" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 max-w-2xl"
        >
          <p className="font-tech text-xs font-bold uppercase tracking-[0.25em] text-[#10B981]">Simulador de economia</p>
          <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Quanto sua loja deixa na rua todo mês?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-white/10 bg-[#1B3579] p-8 sm:p-10"
          >
            <div className="mb-10">
              <div className="mb-4 flex items-baseline justify-between">
                <label htmlFor="fleet-slider" className="font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Carrinhos na frota
                </label>
                <span className="font-display text-3xl font-extrabold text-slate-50" data-testid="fleet-value">{fleet}</span>
              </div>
              <input
                id="fleet-slider"
                type="range"
                min="1"
                max="200"
                step="1"
                value={fleet}
                onChange={(e) => handleFleet(Number(e.target.value))}
                className="w-full"
                data-testid="fleet-slider"
              />
              <div className="mt-2 flex justify-between font-tech text-[10px] uppercase tracking-widest text-slate-500">
                <span>1</span>
                <span>200</span>
              </div>
              <p className="mt-3 text-xs text-slate-400">
                ≈ <strong className="text-slate-200">{lostCarts}</strong> carrinhos perdidos por mês
              </p>
            </div>

            <div>
              <div className="mb-4 flex items-baseline justify-between">
                <label htmlFor="price-slider" className="font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Valor por carrinho
                </label>
                <span className="font-display text-3xl font-extrabold text-slate-50" data-testid="price-value">{brl(price)}</span>
              </div>
              <input
                id="price-slider"
                type="range"
                min="600"
                max="2500"
                step="50"
                value={price}
                onChange={(e) => onPriceChange(Number(e.target.value))}
                className="w-full"
                data-testid="price-slider"
              />
              <div className="mt-2 flex justify-between font-tech text-[10px] uppercase tracking-widest text-slate-500">
                <span>R$ 600</span>
                <span>R$ 2.500</span>
              </div>
            </div>

            <div className="mt-10">
              <div className="mb-4 flex items-baseline justify-between">
                <label htmlFor="ticket-slider" className="font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Ticket médio (Frustração do cliente)
                </label>
                <span className="font-display text-3xl font-extrabold text-slate-50" data-testid="ticket-value">
                  {brl(ticket)}
                </span>
              </div>
              <input
                id="ticket-slider"
                type="range"
                min="1"
                max="100"
                step="1"
                value={ticketUnits}
                onChange={(e) => handleTicketUnits(Number(e.target.value))}
                className="w-full"
                data-testid="ticket-slider"
              />
              <div className="mt-2 flex justify-between font-tech text-[10px] uppercase tracking-widest text-slate-500">
                <span>1 carrinho</span>
                <span>100 carrinhos</span>
              </div>
              <p className="mt-3 text-xs text-slate-400">
                Barras sincronizadas a partir de 1 · cada unidade de carrinho soma R$ 2.300 ({ticketUnits} × R$ 2.300)
              </p>
              <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-relaxed text-slate-300" data-testid="frustration-phrase">
                <strong className="text-[#EF4444]">Frustração do cliente do mês:</strong> cada carrinho
                perdido soma R$ 2.300 em compras que não acontecem.
              </p>
            </div>

            <p className="mt-8 border-t border-white/10 pt-6 text-xs leading-relaxed text-slate-500">
              Cálculo considerando o valor total da frota de carrinhos do seu estabelecimento.
              Valores exibidos por mês.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-6"
          >
            <div className="rounded-2xl border border-[#EF4444]/25 bg-[#1B3579] p-8 sm:p-10" data-testid="loss-result-card">
              <div className="flex items-center gap-3">
                <TrendingDown className="h-5 w-5 text-[#EF4444]" />
                <p className="font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Perda estimada por mês
                </p>
              </div>
              <p className="font-display mt-4 text-4xl font-black tracking-tight text-[#EF4444] sm:text-5xl" data-testid="loss-value">
                {brl(monthlyLoss)}
              </p>
              <p className="mt-2 text-sm text-slate-400" data-testid="loss-formula">
                {fleet} carrinhos na frota × {brl(price)} por carrinho
              </p>
            </div>

            <div className="rounded-2xl border border-[#EF4444]/25 bg-[#1B3579] p-8 sm:p-10" data-testid="ticket-sync-strip">
              <div className="flex items-center gap-3">
                <Receipt className="h-5 w-5 text-[#EF4444]" />
                <p className="font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Ticket médio (Frustração do cliente)
                </p>
              </div>
              <p className="font-display mt-4 text-4xl font-black tracking-tight text-[#EF4444] sm:text-5xl" data-testid="ticket-sync-value">
                {brl(ticket)}
              </p>
              <p className="mt-2 text-sm text-slate-400">
                {ticketUnits} unidades × R$ 2.300 por carrinho
              </p>
            </div>

            <div className="relative overflow-hidden rounded-2xl border border-[#EF4444]/25 bg-[#1B3579] p-8 sm:p-10" data-testid="saving-result-card">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[radial-gradient(circle,rgba(239,68,68,0.22)_0%,transparent_70%)]" />
              <div className="flex items-center gap-3">
                <Wallet className="h-5 w-5 text-[#EF4444]" />
                <p className="font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Prejuízo total no mês
                </p>
              </div>
              <p className="font-display mt-4 text-4xl font-black tracking-tight text-[#EF4444] sm:text-5xl" data-testid="saving-value">
                {brl(monthlyLoss + ticket)}
              </p>
              <p className="mt-2 text-sm text-slate-400">
                {brl(monthlyLoss)} de perda de carrinhos + {brl(ticket)} ticket médio (frustração do cliente)
              </p>
              <a
                href="#contato"
                data-testid="roi-cta-button"
                className="mt-7 inline-flex items-center justify-center rounded-full bg-[#10B981] px-6 py-3.5 font-tech text-xs font-bold uppercase tracking-[0.15em] text-[#1E3A8A] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_35px_rgba(16,185,129,0.4)]"
              >
                Quero reduzir esse prejuízo
              </a>
            </div>

            <div className="rounded-2xl border border-[#EF4444]/25 bg-[#1B3579] p-8 sm:p-10" data-testid="yearly-result-card">
              <div className="flex items-center gap-3">
                <CalendarClock className="h-5 w-5 text-[#EF4444]" />
                <p className="font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Prejuízo total no ano
                </p>
              </div>
              <p className="font-display mt-4 text-4xl font-black tracking-tight text-[#EF4444] sm:text-5xl" data-testid="yearly-value">
                {brl((monthlyLoss + ticket) * 12)}
              </p>
              <p className="mt-2 text-sm text-slate-400">
                {brl(monthlyLoss + ticket)} por mês × 12 meses
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RoiCalculator;
